import includes from 'lodash/includes';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ISelectItem } from '../../models/i-select-item';
import { IMultiUrlImage } from '@cakeiteasy/web-utils';

interface ISelectItemValue {
  id: number;
}

@Component({
  standalone: false,
  selector: 'ui-extra-products-checkbox',
  templateUrl: './ui-extra-products-checkbox.component.html',
  styleUrls: ['./ui-extra-products-checkbox.component.scss']
})
export class UiExtraProductsCheckboxComponent implements OnChanges, OnInit, AfterViewInit {
  @ViewChild('wrap', { static: false }) public wrap: ElementRef;
  @ViewChild('content', { static: false }) public content: ElementRef;

  @Input() public items: ISelectItem[];
  @Input() public showPictures: boolean = false;
  @Input() public selectedItems: ISelectItemValue[];
  @Input() public maxSelections: number | null = null;

  // number of items that can be selected in collapse mode
  @Input() public collapseItemsQuantity = 4;

  @Input()
  public get model() {
    return this.selectedItems;
  }

  public set model(selectedItems: ISelectItemValue[]) {
    this.selectedItems = selectedItems;
    this.modelChange.emit(selectedItems);
  }

  @Input() public placeholder: string;

  @Output() public onImageSelect = new EventEmitter<{ image?: IMultiUrlImage, offsetTop?: number, show?: boolean }>();
  @Output() public modelChange = new EventEmitter<ISelectItemValue[]>();

  public isExpand = false;
  public contentHeight: number | string = 'auto';
  public showingQuantity: number;

  ngOnInit() {
    this.showingQuantity = this.collapseItemsQuantity + 1;
    this._checkExpand();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.items || !this.model) {
      return;
    }

    this._mapModelToItems();
    const itemsToDeselect = this._getItemsToDeselect();

    if (itemsToDeselect.length > 0) {
      itemsToDeselect.forEach(item => item.selected = false);
      this._mapItemsToModel();
    }
  }

  ngAfterViewInit() {
    this._setContentHeight(1000);
  }

  public toggle() {
    this.isExpand = !this.isExpand;

    this._setContentHeight();
  }

  public onChange(item: ISelectItem) {
    this._handleClick(item);

    this._mapItemsToModel();
    this._setContentHeight();
  }

  private _handleClick(item: ISelectItem) {
    if (this._wouldExceedLimit(item)) {
      item.selected = false;
    }
  }

  public selectImage(image, event) {
    const offsetTop = event.target.offsetTop;
    this.onImageSelect.emit({ image, offsetTop });
  }

  public isShowExtraProduct(ind) {
    return this.isExpand || (ind + 1) <= this.showingQuantity;
  }

  public isShowOverlay(ind) {
    return !this.isExpand && (ind + 1) === this.showingQuantity;
  }

  public isDisabled(item: ISelectItem): boolean {
    return !item.selected && this._isLimitReached();
  }

  private _checkExpand() {
    this.isExpand = !this.isExpand && this._isNotShowingItemSelected() || this.items.length <= this.showingQuantity;
  }

  private _isNotShowingItemSelected() {
    return this.items
      .some((val, ind) => val.selected && (ind + 1) >= this.showingQuantity);
  }

  private _setContentHeight(delay?: number) {
    delay = delay || 0;
    setTimeout(() => {
      this.contentHeight = this.content.nativeElement.clientHeight;
    }, delay);
  }

  private _mapModelToItems() {
    const ids = this.model.map(({ id }) => id);
    this.items.forEach((item) => item['selected'] = includes(ids, item.value.id));
  }

  private _getItemsToDeselect(): ISelectItem[] {
    if (!this._hasSelectionLimit()) {
      return [];
    }

    const selectedItems = this.items.filter((item) => item.selected);
    return selectedItems.slice(this.maxSelections);
  }

  private _wouldExceedLimit(justClicked: ISelectItem): boolean {
    return justClicked.selected && this._hasSelectionLimit() && this._countSelected() > this.maxSelections;
  }

  private _isLimitReached(): boolean {
    return this._hasSelectionLimit() && this._countSelected() >= this.maxSelections;
  }

  private _countSelected(): number {
    return this.items.filter((item) => item.selected).length;
  }

  private _hasSelectionLimit(): boolean {
    // - 1 = single selection
    // - null = unlimited
    // - N = limited selection
    return this.maxSelections !== null && this.maxSelections >= 1;
  }

  private _mapItemsToModel() {
    this.model = this.items
      .filter(i => i.selected)
      .map((item) => item.value);
  }
}
